import { useEffect, useState } from "react";
import { api, API, inr, fdate } from "@/api";
import { PageHeader, StatCard, EmptyState } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet, Receipt, ShoppingCart, Scale } from "lucide-react";

export default function GstReports() {
  const [invoices, setInvoices] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/purchases").then((r) => setPurchases(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);

  const inRange = (d) => (!from || d >= from) && (!to || d <= to);
  const sales = invoices.filter((i) => inRange(i.date) && i.status !== "cancelled");
  const buys = purchases.filter((p) => inRange(p.date));
  const taxable = sales.reduce((s, i) => s + (i.taxable ?? i.subtotal), 0);
  const gst = sales.reduce((s, i) => s + i.gst_total, 0);
  const outIgst = sales.filter((i) => i.tax_type === "igst").reduce((s, i) => s + i.gst_total, 0);
  const outCgst = (gst - outIgst) / 2;

  const pTaxable = buys.reduce((s, p) => s + (p.subtotal ?? p.total), 0);
  const itc = buys.reduce((s, p) => s + (p.gst_total ?? 0), 0);
  const itcIgst = buys.filter((p) => p.tax_type === "igst").reduce((s, p) => s + (p.gst_total ?? 0), 0);
  const itcCgst = (itc - itcIgst) / 2;
  const purchaseTotal = buys.reduce((s, p) => s + p.total, 0);
  const netPayable = gst - itc;

  const vMap = Object.fromEntries(vendors.map((v) => [v.vendor_id, v]));
  const csvQ = `from=${from || "0000-01-01"}&to=${to || "9999-12-31"}`;

  return (
    <div data-testid="gst-reports-page">
      <PageHeader title="GST Reports" sub="GSTR-style sales & purchase registers — output GST vs ITC (input tax credit) · download as CSV">
        <a href={`${API}/reports/sales.csv?${csvQ}`} data-testid="download-sales-csv-btn"
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold px-4 py-2 transition-colors duration-200">
          <Download className="w-4 h-4" /> Sales CSV
        </a>
        <a href={`${API}/reports/purchases.csv?${csvQ}`} data-testid="download-purchases-csv-btn"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-emerald-500/50 text-slate-700 hover:text-emerald-700 text-sm font-medium px-4 py-2 transition-colors duration-200">
          <Download className="w-4 h-4" /> ITC / Purchases CSV
        </a>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-4 mb-6 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">From date</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 w-44" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">To date</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 w-44" />
        </div>
        {(from || to) && (
          <button onClick={() => { setFrom(""); setTo(""); }} data-testid="report-clear-dates-btn"
            className="text-xs text-emerald-600 hover:text-emerald-700 pb-2 transition-colors duration-150">
            Clear — show all time
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="report-gst-collected" label="Output GST (Sales)" value={inr(gst)} icon={FileSpreadsheet} tone="blue" />
        <StatCard testid="report-itc" label="ITC — Input Credit" value={inr(itc)} icon={ShoppingCart} tone="amber" />
        <StatCard testid="report-net-gst" label={netPayable >= 0 ? "Net GST Payable" : "Net ITC Carry-forward"} value={inr(Math.abs(netPayable))} icon={Scale} tone={netPayable >= 0 ? "red" : "emerald"} />
        <StatCard testid="report-taxable-sales" label="Taxable Sales" value={inr(taxable)} icon={Receipt} />
      </div>

      {/* Sales register */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto mb-8" data-testid="sales-register">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Sales Register (B2B + B2C) — Output GST</h3>
          <span className="text-xs font-mono2 text-slate-500">{sales.length} invoices</span>
        </div>
        {sales.length === 0 ? (
          <EmptyState icon={Receipt} title="No sales in this period" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]" data-testid="sales-register-table">
              <thead>
                <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3">Invoice #</th><th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Client</th><th className="px-4 py-3">GSTIN</th>
                  <th className="px-4 py-3 text-right">Taxable</th><th className="px-4 py-3 text-right">CGST</th>
                  <th className="px-4 py-3 text-right">SGST</th><th className="px-4 py-3 text-right">IGST</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {sales.map((i) => {
                  const igst = i.tax_type === "igst" ? i.gst_total : 0;
                  const cgst = i.tax_type === "igst" ? 0 : i.gst_total / 2;
                  return (
                    <tr key={i.invoice_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`sales-row-${i.invoice_id}`}>
                      <td className="px-4 py-3 font-mono2 font-semibold text-slate-900">{i.number}</td>
                      <td className="px-4 py-3 text-slate-600">{fdate(i.date)}</td>
                      <td className="px-4 py-3 text-slate-800">{i.client.name} <span className="text-[10px] text-slate-500 uppercase">{i.client.type}</span></td>
                      <td className="px-4 py-3 font-mono2 text-emerald-600 text-xs">{i.client.gstin || "URP"}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-800">{inr(i.taxable ?? i.subtotal)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-600">{inr(cgst)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-600">{inr(cgst)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-600">{inr(igst)}</td>
                      <td className="px-4 py-3 text-right font-mono2 font-bold text-emerald-600">{inr(i.grand_total)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-white/90 font-semibold">
                  <td colSpan={4} className="px-4 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Totals</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(taxable)}</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(outCgst)}</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(outCgst)}</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(outIgst)}</td>
                  <td className="px-4 py-3 text-right font-mono2 font-bold text-emerald-600">{inr(taxable + gst)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Purchase register — ITC */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="purchase-register">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Purchase Register — ITC (Input Tax Credit)</h3>
          <span className="text-xs font-mono2 text-slate-500">{buys.length} entries</span>
        </div>
        {buys.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="No purchases in this period" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]" data-testid="purchase-register-table">
              <thead>
                <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3">Date</th><th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">GSTIN</th><th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3 text-right">Taxable</th><th className="px-4 py-3 text-right">CGST</th>
                  <th className="px-4 py-3 text-right">SGST</th><th className="px-4 py-3 text-right">IGST</th>
                  <th className="px-4 py-3 text-right">ITC</th><th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {buys.map((p) => {
                  const gstAmt = p.gst_total ?? 0;
                  const igst = p.tax_type === "igst" ? gstAmt : 0;
                  const cgst = p.tax_type === "igst" ? 0 : gstAmt / 2;
                  return (
                    <tr key={p.purchase_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`purchase-row-${p.purchase_id}`}>
                      <td className="px-4 py-3 text-slate-600">{fdate(p.date)}</td>
                      <td className="px-4 py-3 text-slate-800">{p.vendor_name}
                        <span className={`ml-2 px-1.5 py-0.5 rounded-full border text-[10px] font-mono2 uppercase ${p.paid ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-amber-500/40 text-amber-600 bg-amber-500/10"}`}>
                          {p.paid ? "Paid" : "Payable"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono2 text-emerald-600 text-xs">{vMap[p.vendor_id]?.gstin || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{p.items.map((i) => `${i.name} ×${i.qty} @${i.gst_rate ?? 0}%`).join(", ")}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-800">{inr(p.subtotal ?? p.total)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-600">{inr(cgst)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-600">{inr(cgst)}</td>
                      <td className="px-4 py-3 text-right font-mono2 text-slate-600">{inr(igst)}</td>
                      <td className="px-4 py-3 text-right font-mono2 font-semibold text-blue-600" data-testid={`itc-${p.purchase_id}`}>{inr(gstAmt)}</td>
                      <td className="px-4 py-3 text-right font-mono2 font-bold text-slate-900">{inr(p.total)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-white/90 font-semibold">
                  <td colSpan={4} className="px-4 py-3 text-xs font-mono2 uppercase tracking-wider text-slate-500">Totals (ITC)</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(pTaxable)}</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(itcCgst)}</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(itcCgst)}</td>
                  <td className="px-4 py-3 text-right font-mono2 text-slate-900">{inr(itcIgst)}</td>
                  <td className="px-4 py-3 text-right font-mono2 font-bold text-blue-600" data-testid="itc-total">{inr(itc)}</td>
                  <td className="px-4 py-3 text-right font-mono2 font-bold text-amber-600">{inr(purchaseTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
